/* eslint-disable react/no-multi-comp -- allow subrouting components */
import { CompassOutlined } from "@ant-design/icons";
import { Result, Spin } from "antd";
import { lazy, Suspense, createElement } from "react";
import { DataRouteObject, Navigate, useRouteError } from "react-router";
import AppLayout from "./AppLayout";
import { QueryParamProvider } from "use-query-params";
import HomeButton from "./components/UI/HomeButton";
import RetryButton from "./components/UI/RetryButton";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

function LoadingSpin() {
  return (
    <Spin
      spinning
      style={{ textAlign: "center", width: "100%", marginTop: "100px" }}
    />
  );
}

export function NotFound() {
  return (
    <Result
      status="404"
      title="404"
      subTitle="Sorry, the page you visited does not exist."
      extra={<HomeButton />}
    />
  );
}

export function ErrorBoundary() {
  const error: unknown = useRouteError();
  return (
    <Result
      status="500"
      title={error?.toString()}
      subTitle="Sorry, something went wrong"
      extra={<RetryButton />}
    />
  );
}

export const routes: DataRouteObject[] = [
  {
    id: "root",
    path: "/",
    element: (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <AppLayout />
      </QueryParamProvider>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        id: "home",
        path: "/",
        index: true,
        element: <Navigate to="/simulations" />,
        errorElement: <ErrorBoundary />,
      },
      {
        id: "simulations",
        path: "/simulations",
        element: (
          <Suspense fallback={<LoadingSpin />}>
            {createElement(
              lazy(
                () => import("./components/SimulationsPage/SimulationsPage"),
              ),
              {},
            )}
          </Suspense>
        ),
        handle: {
          title: "Infeed sizing  simulator",
          icon: CompassOutlined,
        },
        children: [
          {
            id: "simulations-index",
            index: true,
            element: (
              <Suspense>
                {createElement(
                  lazy(
                    () =>
                      import("./components/SimulationsPage/SimulationsIndex"),
                  ),
                  {},
                )}
              </Suspense>
            ),
          },
          {
            id: "simulation",
            path: ":id",
            element: (
              <Suspense fallback={<LoadingSpin />}>
                {createElement(
                  lazy(
                    () =>
                      import(
                        "./components/SimulationsPage/Simulation/Simulation"
                      ),
                  ),
                  {},
                )}
              </Suspense>
            ),
          },
        ],
      },
      {
        id: "not-found",
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

const appendChildren = <R extends typeof routes>(
  routes: R,
): DataRouteObject[] =>
  routes
    .map((route) => (route?.children ? appendChildren(route.children) : route))
    .flat();

export const navigationMenuRoutes = appendChildren(routes)
  .filter((route) => !!route.handle?.icon)
  .map((route) => ({
    id: route.id,
    path: route.path,
    title: route.handle?.title,
    icon: route.handle?.icon,
    disabled: route.handle?.disabled,
  }));
